<template>
  <loader :is-loading="isLoading" />
  <lf-modal large :close="() => emit('close')">
    <template #header>
      <div class="flex items-center space-x-4">
        <lf-h1>{{ $t("CHECKOUT.SET_CHECKOUT_TEMPLATE") }}</lf-h1>
        <lf-tag size="lg" class="text-label">{{ funder.full_name }}</lf-tag>
        <lf-tag size="lg" class="text-label">
          {{ PRODUCT_TYPE_OPTIONS[offerTemplate.product] }}
        </lf-tag>
      </div>
    </template>
    <template #content>
      <div class="space-y-6 p-6">
        <search-input
          v-model="search"
          class="inline-block max-w-96"
          no-padding
          no-margin
          data-cy="search-input"
        />
        <div v-if="!!filteredCheckouts.length" class="grid grid-cols-2 gap-4">
          <button
            v-for="template in filteredCheckouts"
            :key="template.id"
            class="block cursor-pointer divide-y divide-gray-200 rounded-md border border-gray-200 transition-colors"
            :class="{
              'border-primary bg-primary-background-active':
                template.id === selectedCheckoutTemplateId,
            }"
            @click="selectedCheckoutTemplateId = template.id!"
          >
            <div class="flex items-center space-x-4 px-6 py-4">
              <input
                type="radio"
                class="cursor-pointer"
                :checked="template.id === selectedCheckoutTemplateId"
              />
              <div class="font-semibold text-headline">{{ template.name }}</div>
            </div>
            <div
              v-tooltip="
                template.description && template.description?.length > 140
                  ? template.description
                  : undefined
              "
              class="h-full px-6 py-4 text-left"
            >
              {{ truncateWithEllipsis(template?.description ?? "", 140) }}
            </div>
          </button>
        </div>
        <div v-else>
          {{ $t("CHECKOUT.NO_TEMPLATES_AVAILALBE_FOR_FUNDER") }}
        </div>
      </div>
      <div class="flex justify-end border-t border-gray-200 py-6">
        <modal-buttons :on-cancel="() => emit('close')" :on-save="save" />
      </div>
    </template>
  </lf-modal>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";

import {
  OFFER_SCHEMA_VERSION,
  PRODUCT_TYPE_OPTIONS,
} from "@/helpers/constants/offers";
import { truncateWithEllipsis } from "@/helpers/formatting";
import offerTemplateService from "@/services/modules/offerTemplate";

import Loader from "@/components/ui/Loader.vue";
import LfModal from "@/components/ui/Modal.vue";
import ModalButtons from "@/components/ui/ModalButtons.vue";
import SearchInput from "@/components/ui/inputs/SearchInput.vue";
import LfH1 from "@/components/ui/text/LfHeading1.vue";
import LfTag from "@/components/ui/text/LfTag.vue";

import type { CheckoutTemplateBase } from "@/models/checkout";
import type { IFunder } from "@/models/funders";
import type { OfferTemplate } from "@/models/offers";

const emit = defineEmits<{
  close: [];
  templateUpdated: [OfferTemplate];
}>();

const { offerTemplate, checkoutTemplates, funder } = defineProps<{
  offerTemplate: OfferTemplate;
  checkoutTemplates: CheckoutTemplateBase[];
  funder: IFunder;
}>();

const isLoading = ref(false);
const selectedCheckoutTemplateId = ref(
  offerTemplate.content.checkout_template_id ?? null
);
const search = ref("");

const filteredCheckouts = computed(() => {
  if (!search.value) {
    return checkoutTemplates;
  }

  return checkoutTemplates.filter((checkoutTemplate) =>
    checkoutTemplate.name
      .toLocaleLowerCase()
      .includes(search.value.trim().toLocaleLowerCase())
  );
});

const save = async () => {
  if (
    !!selectedCheckoutTemplateId.value &&
    selectedCheckoutTemplateId.value !==
      offerTemplate.content.checkout_template_id
  ) {
    const updatedTemplate = await offerTemplateService.updateTemplate({
      ...offerTemplate,
      content: {
        ...offerTemplate.content,
        checkout_template_id: selectedCheckoutTemplateId.value,
        version: OFFER_SCHEMA_VERSION,
      },
    });

    emit("templateUpdated", updatedTemplate);
  }

  emit("close");
};
</script>
